import { FC, useState } from 'react';
import { TextField, Button, Chip, CircularProgress } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailListEditorProps {
  label: string;                 // heading shown above the input
  emails: string[];              // current list
  onChange: (next: string[]) => void; // called with the new list on add/remove (parent persists)
  saving?: boolean;              // disables inputs while a save is in flight
  emptyText?: string;            // message shown when the list is empty
}

/**
 * Reusable email-list editor: a validated input + Add button + removable chips.
 * The parent owns the list and persistence; this component only edits it.
 */
const EmailListEditor: FC<EmailListEditorProps> = ({ label, emails, onChange, saving = false, emptyText = 'No emails added.' }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const email = input.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) { setError('Enter a valid email address'); return; }
    if (emails.includes(email)) { setError('This email is already added'); return; }
    onChange([...emails, email]);
    setInput('');
    setError(null);
  };

  const remove = (email: string) => onChange(emails.filter((e) => e !== email));

  return (
    <div>
      <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>{label}</h5>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 480 }}>
        <TextField
          size="small"
          variant="standard"
          placeholder="Enter email address"
          value={input}
          onChange={(e) => { setInput(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          error={!!error}
          helperText={error || ' '}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={add}
          disabled={saving || !input.trim()}
          sx={{ mt: 0.25, textTransform: 'none', color: '#059669', borderColor: '#059669' }}
        >
          Add
        </Button>
        {saving ? <CircularProgress size={16} style={{ marginTop: 6 }} /> : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
        {emails.length === 0
          ? <span style={{ fontSize: 13, color: '#9da7b7' }}>{emptyText}</span>
          : emails.map((email) => (
            <Chip key={email} label={email} onDelete={() => remove(email)} disabled={saving} size="small" />
          ))}
      </div>
    </div>
  );
};

export default EmailListEditor;
